'use client';

import { useEffect, useRef, useState } from 'react';
import { suggestionsFor } from '@/content/aide/assistant-suggestions';
import { ASSISTANT_LIMITS } from '@/lib/schemas/assistant.schema';
import { appliquer, ecarter, passer2D, viser } from '@/lib/copilote/pont';
import { AssistantMessage } from './AssistantMessage';
import { AssistantProposal } from './AssistantProposal';
import { canSend, lastRequest } from './assistant-state';
import { useSupportPanel } from './SupportProvider';

const chip = 'rounded-full border border-[var(--ink-200)] bg-[var(--paper)] px-[11px] py-[5px] text-xs text-[var(--ink-800)] hover:border-[var(--accent)] hover:text-[var(--accent-700)]';
const chipPrimary = 'rounded-full border border-[var(--accent-100)] bg-[var(--accent-50)] px-[11px] py-[5px] text-xs font-semibold text-[var(--accent-700)] hover:border-[var(--accent)]';
const banner = {
  warn: 'flex gap-2 rounded-[10px] border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-900',
  err: 'flex gap-2 rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs leading-relaxed text-rose-800',
  ok: 'flex gap-2 rounded-[10px] border border-green-200 bg-green-50 px-3 py-2.5 text-xs leading-relaxed text-green-800',
};

/** L'onglet « Assistant » du panneau du support (chantier ASI, prototype P01 a P04, P06, P07) */
export function AssistantTab() {
  const { conversation, askAssistant, stopAssistant, dispatchAssistant, editorTool, openFromAssistant, isOpen } = useSupportPanel();
  const [question, setQuestion] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [aiming, setAiming] = useState(false);
  const { messages, status } = conversation;
  const last = messages.at(-1);
  const lastQuestion = lastRequest(conversation);
  const busy = status === 'thinking' || status === 'waking' || status === 'wakingLong' || status === 'writing';
  const length = question.trim().length;
  const over = length > ASSISTANT_LIMITS.questionMaxChars;
  const toSupport = openFromAssistant;

  // La conversation suit la reponse qui s'ecrit
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [messages, status, isOpen]);

  const send = (q = question) => {
    if (!canSend(q, conversation)) return;
    askAssistant(q);
    if (q === question) setQuestion('');
  };
  const suggestions = suggestionsFor(editorTool);
  // C7 : un clic sur le plan montre l'endroit ; Echap annule
  const showOnPlan = async () => {
    if (!lastQuestion) return;
    setAiming(true);
    const point = await viser();
    setAiming(false);
    if (point) askAssistant(lastQuestion, { point });
  };
  // P06 : la vue 2D, puis la meme demande
  const to2dAndAsk = () => {
    if (!lastQuestion) return;
    passer2D();
    setTimeout(() => send(lastQuestion), 100);
  };

  return (
    <>
      <div ref={bodyRef} className="flex min-h-[260px] flex-1 flex-col gap-3 overflow-y-auto px-4 py-3.5">
        {messages.length === 0 ? (
          <div className="flex flex-col gap-2.5">
            <h3 className="m-0 text-[15px] font-bold text-[var(--ink-900)]">{editorTool ? 'Posez une question, ou demandez une action' : 'Posez votre question sur archifacil'}</h3>
            <p className="m-0 text-[var(--ink-600)]">{editorTool
              ? 'L\'assistant répond à partir du centre d\'aide, et peut aussi modifier votre plan : il vous montre d\'abord ce qu\'il va faire.'
              : <>L&apos;assistant répond à partir du centre d&apos;aide, et vous donne le lien du tutoriel qui en parle.</>}</p>
            {status !== 'limit' && (
              <div className="flex flex-col items-start gap-1.5">
                <small className="text-[11px] font-bold uppercase tracking-[0.04em] text-[var(--ink-500)]">{suggestions.label}</small>
                {suggestions.questions.map(s => (
                  <button key={s} type="button" className={`${chip} text-left`} onClick={() => send(s)}>{s}</button>
                ))}
              </div>
            )}
            <p className="m-0 text-[11px] text-[var(--ink-500)]">{editorTool
              ? 'L\'assistant ne change rien sans votre accord. Ctrl+Z annule ce qu\'il a fait.'
              : 'L\'assistant peut se tromper. Pour un problème dans votre projet, écrivez au support : onglet « Nouvelle demande ».'}</p>
          </div>
        ) : (
          <>
            <div className="flex justify-end">
              <button type="button" className={chip} onClick={() => { stopAssistant(); ecarter(); dispatchAssistant({ type: 'reset' }); }}>↺ Nouvelle conversation</button>
            </div>
            {messages.map(m => <AssistantMessage key={m.id} message={m} />)}
          </>
        )}

        {status === 'waking' && (
          <div className={banner.warn} role="status">
            <span>⏳</span>
            <div>
              <b>L&apos;assistant se réveille</b>, environ une minute. Il se met en veille quand personne ne lui parle. Votre question partira dès qu&apos;il sera prêt.
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-[3px] bg-amber-200"><i className="block h-full w-1/2 animate-pulse rounded-[3px] bg-amber-500" /></div>
            </div>
          </div>
        )}
        {status === 'wakingLong' && (
          <>
            <div className={banner.warn} role="status">
              <span>⏳</span>
              <div><b>C&apos;est plus long que d&apos;habitude.</b> Vous pouvez attendre encore un peu, ou écrire au support : votre question y sera reprise.</div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button type="button" className={chip} onClick={() => { stopAssistant(); toSupport(); }}>Écrire au support</button>
              <button type="button" className={chip} onClick={stopAssistant}>Annuler la question</button>
            </div>
          </>
        )}

        {!busy && last?.proposal && (
          <AssistantProposal
            proposal={last.proposal}
            onApply={() => { appliquer(); }}
            onChange={() => inputRef.current?.focus()}
            onDismiss={ecarter}
            onRedo={() => lastQuestion && send(lastQuestion)}
          />
        )}
        {!busy && last?.where && (
          <div><button type="button" className={chipPrimary} disabled={aiming} onClick={showOnPlan}>⌖ Montrer sur le plan</button></div>
        )}
        {/* P06 : seulement quand la reponse renvoie a la vue 2D : une question reste une question (C2) */}
        {!busy && last?.to2d && /\b2D\b/.test(last.text) && (
          <div><button type="button" className={chipPrimary} onClick={to2dAndAsk}>Passer en 2D et proposer</button></div>
        )}
        {!busy && last?.role === 'assistant' && last.state === 'done' && !last.proposal && !last.where && (
          last.resolved ? (
            <div className={banner.ok}><span>✓</span><div>Merci ! Une autre question ? Écrivez-la ci-dessous.</div></div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              <button type="button" className={chipPrimary} onClick={() => dispatchAssistant({ type: 'resolved', id: last.id })}>C&apos;est résolu</button>
              <button type="button" className={chip} onClick={toSupport}>Ce n&apos;est pas résolu, écrire au support</button>
            </div>
          )
        )}
        {!busy && last?.role === 'assistant' && last.state === 'stopped' && (
          <>
            <p className="m-0 self-center text-[11px] text-[var(--ink-400)]">Réponse arrêtée</p>
            {lastQuestion && <div><button type="button" className={chip} onClick={() => send(lastQuestion)}>Reprendre la réponse</button></div>}
          </>
        )}
        {!busy && last?.role === 'assistant' && last.state === 'interrupted' && (
          <>
            <div className={banner.err} role="alert"><span>!</span><div><b>La réponse s&apos;est interrompue.</b> Reposez la question : si l&apos;assistant s&apos;est mis en veille, il se réveillera.</div></div>
            {lastQuestion && <div><button type="button" className={chipPrimary} onClick={() => send(lastQuestion)}>Reposer la question</button></div>}
          </>
        )}
        {!busy && last?.role === 'assistant' && last.state === 'unreadable' && (
          <>
            <div className={banner.err} role="alert"><span>!</span><div><b>Je n&apos;ai pas réussi à préparer cette action.</b> Reformulez votre demande, ou faites-la à la main.</div></div>
            {lastQuestion && <div><button type="button" className={chipPrimary} onClick={() => send(lastQuestion)}>Réessayer</button></div>}
          </>
        )}
        {!busy && last?.role === 'assistant' && last.state === 'unavailable' && (
          <>
            <div className={banner.err} role="alert"><span>!</span><div><b>L&apos;assistant ne répond pas pour le moment.</b> Réessayez dans quelques minutes, ou écrivez au support.</div></div>
            <div className="flex flex-wrap gap-1.5">
              {lastQuestion && <button type="button" className={chipPrimary} onClick={() => send(lastQuestion)}>Réessayer</button>}
              <button type="button" className={chip} onClick={toSupport}>Écrire au support</button>
            </div>
          </>
        )}
        {status === 'limit' && (
          <>
            <div className={banner.warn} role="status"><span>⏳</span><div>{conversation.limitMessage}</div></div>
            <div><button type="button" className={chipPrimary} onClick={toSupport}>Écrire au support</button></div>
          </>
        )}
      </div>

      {length > ASSISTANT_LIMITS.questionMaxChars * 0.8 && (
        <div className="flex justify-between px-3.5 pb-2 text-[11px] text-[var(--ink-500)]">
          <span>Une question courte obtient une meilleure réponse.</span>
          <span className={over ? 'font-bold text-[var(--danger)]' : ''}>{length.toLocaleString('fr-FR')} / {ASSISTANT_LIMITS.questionMaxChars.toLocaleString('fr-FR')}</span>
        </div>
      )}
      <div className="flex items-end gap-2 border-t border-[var(--ink-100)] px-3 py-2.5">
        <label htmlFor="assistant-question" className="sr-only">Votre question</label>
        <textarea
          id="assistant-question"
          ref={inputRef}
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          disabled={status === 'limit' || aiming}
          placeholder={aiming ? 'Cliquez sur le plan…' : status === 'limit' ? 'Limite du jour atteinte' : status === 'waking' || status === 'wakingLong' ? 'Patientez, l\'assistant se réveille…' : 'Posez votre question…'}
          rows={1}
          className={`h-10 min-w-0 flex-1 resize-none rounded-[10px] border bg-[var(--paper)] px-2.5 py-[9px] text-[var(--ink-900)] outline-none focus:border-[var(--accent)] disabled:bg-[var(--ink-50)] ${over ? 'border-[var(--danger)]' : 'border-[var(--ink-200)]'}`}
        />
        {busy ? (
          <button type="button" onClick={stopAssistant} className="h-10 rounded-[10px] border border-[var(--ink-200)] bg-[var(--paper)] px-3 font-bold text-[var(--ink-800)]">Arrêter</button>
        ) : (
          <button type="button" onClick={() => send()} disabled={!canSend(question, conversation)} className="h-10 rounded-[10px] bg-[var(--accent)] px-3.5 font-bold text-white disabled:bg-[var(--ink-300)]">Envoyer</button>
        )}
      </div>
    </>
  );
}
